import { useTheme } from '../contexts/ThemeContext';

export default function Home() {
  const { darkMode } = useTheme();
  return (
    <div className={`w-full ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Hero Section */}
      <div className="relative overflow-hidden w-full">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-12 sm:py-20 lg:py-28">
          <div className="text-center max-w-5xl mx-auto">
            <h1 className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-6 sm:mb-8 leading-tight tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Transform Content into
              <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent block mt-2"> Learning Experience</span>
            </h1>
            <p className={`text-base sm:text-lg md:text-xl max-w-3xl mx-auto mb-8 sm:mb-12 leading-relaxed font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Content2Learn customize your learning experience based on your preferences to help you achieve your learning goals faster.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
              <a
                href="/content2learn"
                className="hidden sm:inline-block px-8 sm:px-12 py-3 sm:py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-base sm:text-lg rounded-xl sm:rounded-2xl font-bold hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Get Started
              </a>
              <a
                href="/discovery"
                className="px-8 sm:px-12 py-3 sm:py-4 bg-white text-green-600 text-base sm:text-lg rounded-xl sm:rounded-2xl font-bold border-2 border-green-600 hover:bg-green-50 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Explore Content
              </a>
            </div>
          </div>
        </div>
        <div className="absolute inset-0 pointer-events-none"></div>
      </div>

      {/* Demo Video Section */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="aspect-w-16 aspect-h-9 max-w-5xl mx-auto h-[280px] sm:h-[400px] md:h-[480px] lg:h-[600px]">
          <iframe
            src="https://www.youtube.com/embed/qGH0J2xzdTE"
            title="Content2Learn Demo"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl"
          />
        </div>
      </div>

      {/* Features Section */}
      <div className={`w-full px-4 sm:px-6 lg:px-8 py-10 sm:py-16 md:py-20 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-10 ${darkMode ? 'text-white' : 'text-gray-900'}`}>How It Works</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-7xl mx-auto">
          <div className={`p-6 sm:p-8 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-green-600 text-xl sm:text-2xl font-bold mb-3 sm:mb-4 flex items-center">
              <span className="bg-green-100 rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center mr-3 sm:mr-4 text-green-600">1</span>
              Input Content
            </div>
            <p className={`text-base sm:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Paste any URL or text content you want to learn from. Support for articles, videos, and more.
            </p>
          </div>
          <div className={`p-6 sm:p-8 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-green-600 text-xl sm:text-2xl font-bold mb-3 sm:mb-4 flex items-center">
              <span className="bg-green-100 rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center mr-3 sm:mr-4 text-green-600">2</span>
              Process
            </div>
            <p className={`text-base sm:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Our AI automatically structures the content into digestible learning materials.
            </p>
          </div>
          <div className={`p-6 sm:p-8 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-green-600 text-xl sm:text-2xl font-bold mb-3 sm:mb-4 flex items-center">
              <span className="bg-green-100 rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center mr-3 sm:mr-4 text-green-600">3</span>
              Learn
            </div>
            <p className={`text-base sm:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Access your personalized learning materials anytime. Track progress and review.
            </p>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className={`w-full px-4 sm:px-6 lg:px-8 py-10 sm:py-16 md:py-20 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto">
          <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-10 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Why Choose Content2Learn</h2>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              {
                title: "Save Time",
                description: "Convert content into structured learning materials instantly"
              },
              {
                title: "Learn Better",
                description: "Organized content helps you retain information more effectively"
              },
              {
                title: "Stay Organized",
                description: "Keep all your learning materials in one place"
              },
              {
                title: "Share Knowledge",
                description: "Collaborate and share processed content with others"
              }
            ].map((benefit, index) => (
              <div key={index} className="text-center">
                <h3 className={`text-lg sm:text-xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{benefit.title}</h3>
                <p className={`text-sm sm:text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className={`w-full px-4 sm:px-6 lg:px-8 py-10 sm:py-16 md:py-20 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto text-center">
          <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Ready to Transform Your Learning?</h2>
          <a
            href="/signup"
            className="inline-block px-8 sm:px-12 py-3 sm:py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-base sm:text-lg rounded-xl sm:rounded-2xl font-bold hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            Create Free Account
          </a>
        </div>
      </div>
    </div>
  );
}